<template>
  <span v-if="badges.length" class="certificate-type-badges">
    <span
      v-for="(badge, idx) in badges"
      :key="`${badge.code}-${idx}`"
      class="certificate-type-badge-wrap"
      @mouseenter="openPopover(idx, $event)"
      @mouseleave="closePopover"
      @focusin="openPopover(idx, $event)"
      @focusout="onFocusOut"
    >
      <span
        :ref="'trigger-' + idx"
        class="badge certificate-type-badge"
        :class="badgeClass(badge.code)"
        tabindex="0"
        role="img"
        :aria-label="ariaLabel(badge)"
        :title="badge.contact || badge.code"
      >
        {{ badge.code }}
      </span>
    </span>
    <span
      v-show="popoverVisible"
      ref="popover"
      class="certificate-type-popover"
      role="tooltip"
      @mouseenter="keepOpen"
      @mouseleave="closePopover"
    >{{ popoverText }}</span>
  </span>
  <span v-else class="text-muted">-</span>
</template>

<script>
function parseCertificateTypeValue(value) {
  const raw = value != null ? String(value) : "";
  if (!raw.trim()) return [];
  return raw
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const m = line.match(/^([A-Za-z0-9]+)\s*[—–\-]\s*(.*)$/);
      if (m) {
        return { code: m[1].toUpperCase(), contact: String(m[2] || "").trim() };
      }
      return { code: line.toUpperCase(), contact: "" };
    });
}

const DERIVED_CERT_KEYS = [
  { key: "expenses_bsc_certificate", code: "BSC" },
  { key: "expenses_besc_cameroon", code: "BESC" },
  { key: "expenses_cnca_certificate", code: "CNCA" },
  { key: "expenses_anor_certificate", code: "ANOR" },
];

function contactNameFromRef(ref) {
  if (!ref) return "";
  if (typeof ref === "object") {
    if (ref.contact && typeof ref.contact === "object") {
      return ref.contact.name || ref.contact.label || "";
    }
    if (ref.contact && typeof ref.contact === "string") return "";
    return ref.name || ref.label || "";
  }
  return "";
}

export default {
  name: "CertificateTypeBadges",
  props: {
    value: {
      type: [String, Number],
      default: "",
    },
    item: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      popoverVisible: false,
      popoverText: "",
      activeIdx: -1,
    };
  },
  computed: {
    badges() {
      const fromValue = parseCertificateTypeValue(this.value);
      if (fromValue.length) return fromValue;
      if (!this.item || typeof this.item !== "object") return [];
      const out = [];
      for (const def of DERIVED_CERT_KEYS) {
        const ref = this.item[def.key];
        if (ref == null || ref === "") continue;
        out.push({
          code: def.code,
          contact: contactNameFromRef(ref),
        });
      }
      return out;
    },
  },
  beforeDestroy() {
    clearTimeout(this._closeTimer);
    this.detachScrollListener();
    this.removePopoverFromBody();
  },
  methods: {
    badgeClass(code) {
      const c = String(code || "").toUpperCase();
      if (c === "BSC") return "bg-primary";
      if (c === "BESC") return "bg-info text-dark";
      if (c === "CNCA") return "bg-success";
      if (c === "ANOR") return "bg-warning text-dark";
      return "bg-secondary";
    },
    ariaLabel(badge) {
      if (badge.contact) return `${badge.code}: ${badge.contact}`;
      return badge.code;
    },
    openPopover(idx) {
      const badge = this.badges[idx];
      if (!badge) return;
      const text = badge.contact
        ? `${badge.code}: ${badge.contact}`
        : badge.code;
      clearTimeout(this._closeTimer);
      this.activeIdx = idx;
      this.popoverText = text;
      this.popoverVisible = true;
      this.$nextTick(() => {
        this.ensurePopoverInBody();
        this.updatePopoverPosition(idx);
        this.attachScrollListener();
      });
    },
    keepOpen() {
      clearTimeout(this._closeTimer);
    },
    closePopover() {
      clearTimeout(this._closeTimer);
      this._closeTimer = setTimeout(() => {
        this.popoverVisible = false;
        this.activeIdx = -1;
        this.detachScrollListener();
      }, 80);
    },
    onFocusOut(event) {
      const root = this.$el;
      if (root && root.contains(event.relatedTarget)) return;
      this.closePopover();
    },
    attachScrollListener() {
      if (this._scrollListener) return;
      this._scrollListener = () => {
        if (this.popoverVisible && this.activeIdx >= 0) {
          this.updatePopoverPosition(this.activeIdx);
        }
      };
      window.addEventListener("scroll", this._scrollListener, true);
      window.addEventListener("resize", this._scrollListener);
    },
    detachScrollListener() {
      if (!this._scrollListener) return;
      window.removeEventListener("scroll", this._scrollListener, true);
      window.removeEventListener("resize", this._scrollListener);
      this._scrollListener = null;
    },
    ensurePopoverInBody() {
      const popover = this.$refs.popover;
      if (!popover || popover.parentNode === document.body) return;
      document.body.appendChild(popover);
    },
    removePopoverFromBody() {
      const popover = this.$refs.popover;
      if (!popover || popover.parentNode !== document.body) return;
      popover.parentNode.removeChild(popover);
    },
    updatePopoverPosition(idx) {
      const triggerRef = this.$refs["trigger-" + idx];
      const trigger = Array.isArray(triggerRef) ? triggerRef[0] : triggerRef;
      const popover = this.$refs.popover;
      if (!trigger || !popover) return;

      const rect = trigger.getBoundingClientRect();
      const gap = 8;
      const popoverWidth = popover.offsetWidth || 220;
      const left = Math.min(
        Math.max(8, rect.left + rect.width / 2 - popoverWidth / 2),
        window.innerWidth - popoverWidth - 8
      );
      const top = Math.max(8, rect.top - gap);

      popover.style.position = "fixed";
      popover.style.top = `${top}px`;
      popover.style.left = `${left}px`;
      popover.style.transform = "translateY(-100%)";
      popover.style.zIndex = "1080";
    },
  },
};
</script>

<style scoped>
.certificate-type-badges {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}

.certificate-type-badge-wrap {
  display: inline-flex;
}

.certificate-type-badge {
  cursor: default;
  font-weight: 600;
  font-size: 0.7rem;
  padding: 0.25em 0.45em;
  user-select: none;
}

.certificate-type-popover {
  position: relative;
  min-width: 140px;
  max-width: 320px;
  padding: 8px 10px;
  font-size: 0.75rem;
  font-weight: normal;
  line-height: 1.35;
  text-align: left;
  white-space: normal;
  color: #fff;
  background: #212529;
  border-radius: 6px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
  pointer-events: auto;
}

.certificate-type-popover::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: #212529;
}
</style>
